import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { ChatMessage } from "../types";
import { Socket } from "socket.io-client";

/**
 * Custom hook to handle chat functionality
 * @param socket - Socket instance to use for communication
 * @param streamId - ID of the current stream
 * @param userId - ID of the current user
 * @param username - Username of the current user
 * @param avatarUrl - Avatar URL of the current user
 * @returns Chat state and functions
 */
export const useChatHandler = (
  socket: Socket | null,
  streamId: string | undefined,
  userId: string,
  username: string,
  avatarUrl?: string
) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [totalViewers, setTotalViewers] = useState(0);
  const [likes, setLikes] = useState(0);
  const [totalGiftValue, setTotalGiftValue] = useState(0);

  // Subscribe to socket events
  useEffect(() => {
    if (!socket || !streamId) return;

    // Join the stream room and handle initial state
    socket.emit(
      "join-stream",
      { streamId, userId, username, avatarUrl },
      (response: any) => {
        if (response && response.success && response.roomState) {
          const { roomState } = response;
          setLikes(roomState.likeCount || 0);
          setTotalViewers(roomState.viewerCount || 0);
          setTotalGiftValue(roomState.totalGiftValue || 0);

          if (roomState.recentMessages) {
            const mappedMessages = roomState.recentMessages.map((msg: any) => ({
              id: msg.id || Date.now().toString(),
              username: msg.username,
              message: msg.message,
              avatar: msg.avatarUrl,
              timestamp: new Date(msg.createdAt).getTime(),
            }));
            // Ensure chronological order if backend returns reverse
            // Backend returns reverse (newest first) based on code reading?
            // stream-room.service.ts says: .reverse() // Reverse to get chronological order.
            // So it should be oldest first.
            setMessages(mappedMessages);
          }
        }
      }
    );

    // Listeners
    const handleNewMessage = (data: any) => {
      setMessages((prev) => [
        ...prev,
        {
          id: data.id,
          username: data.username,
          message: data.message,
          avatar: data.avatarUrl,
          timestamp: new Date(data.createdAt).getTime(),
        },
      ]);
    };

    const handleViewerCount = (data: { viewerCount: number }) => {
      setTotalViewers(data.viewerCount);
    };

    const handleLikeCount = (data: {
      likeCount: number;
      likedBy?: string;
      avatarUrl?: string; // Avatar URL from backend
    }) => {
      setLikes(data.likeCount);
      if (data.likedBy) {
        setMessages((prev) => [
          ...prev,
          {
            id: `like-${Date.now()}-${Math.random()}`,
            username: data.likedBy!,
            message: "liked the stream! ❤️",
            avatar:
              data.avatarUrl ||
              "https://squadify-avatar.s3.eu-west-2.amazonaws.com/panda.svg",
            timestamp: Date.now(),
          },
        ]);
      }
    };

    const handleNewGift = (data: any) => {
      // data: { ..., giftValue, totalGiftValue, ... }
      if (data.totalGiftValue !== undefined) {
        setTotalGiftValue(data.totalGiftValue);
      } else if (data.giftValue) {
        setTotalGiftValue((prev) => prev + Number(data.giftValue));
      }

      setMessages((prev) => [
        ...prev,
        {
          id: data.id,
          username: data.senderUsername,
          message: `sent a ${data.giftName}! 🎁`,
          avatar:
            data.senderAvatarUrl ||
            "https://squadify-avatar.s3.eu-west-2.amazonaws.com/panda.svg",
          timestamp: new Date(data.createdAt).getTime(),
        },
      ]);
    };

    socket.on("new-message", handleNewMessage);
    socket.on("viewer-count", handleViewerCount);
    socket.on("like-count", handleLikeCount);
    socket.on("new-gift", handleNewGift);

    return () => {
      socket.emit("leave-stream");
      socket.off("new-message", handleNewMessage);
      socket.off("viewer-count", handleViewerCount);
      socket.off("like-count", handleLikeCount);
      socket.off("new-gift", handleNewGift);
    };
  }, [socket, streamId, userId, username, avatarUrl]);

  // ... (sendMessage, sendLike implementation remains same) ...

  const sendMessage = useCallback(
    (text: string) => {
      if (!socket || !streamId) return;

      // Optimistic update?
      // For now wait for server broadcast to avoid duplicates or manage IDs carefully.
      // But typically local echo is nice. Let's just emit and wait for 'new-message' due to broadcast.
      socket.emit("chat-message", { message: text });
    },
    [socket, streamId]
  );

  const sendLike = useCallback(() => {
    if (!socket || !streamId) return;
    socket.emit("like");
    // Local feedback
    // setLikes((prev) => prev + 1); // Server will broadcast total count
    toast.success("❤️");
  }, [socket, streamId]);

  return {
    messages,
    sendMessage,
    sendLike,
    totalViewers,
    likes,
    totalGiftValue,
  };
};
